import { useEffect, useRef, useState } from 'react';

import Icon from './Icon.jsx';

/**
 * The hymn player — a bar in the bottom-left corner.
 *
 * Collapsed it is a now-playing pill: badge, what is playing, and the transport
 * inline. Tapping the title opens the hymnal above it. The close button retires
 * it to a single disc so it can be brought back without hunting for a menu.
 *
 * It floats above the app rather than living on a screen, so a hymn keeps
 * playing while you move between flashcards, the quiz and the story — which is
 * the whole point of music while you study.
 */
export default function HymnDock({ hymnal, music }) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [mixer, setMixer] = useState(false);

  const rootRef = useRef(null);
  const listRef = useRef(null);

  const { hymn, hymns, playing, position, error, supported } = hymnal;

  // Keep the playing hymn in view when it changes underneath an open panel.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector('.dock__track.is-current')
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, hymn.id]);

  // Click-away and Escape close whichever popover is showing.
  useEffect(() => {
    if (!open && !mixer) return undefined;

    const onPointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      setOpen(false);
      setMixer(false);
    };
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      setMixer(false);
      setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, mixer]);

  // Dismissed: a single disc that brings the bar back.
  if (dismissed) {
    return (
      <div className="dock dock--tucked" ref={rootRef}>
        <button
          type="button"
          className={`dock__restore${playing ? ' is-playing' : ''}`}
          onClick={() => setDismissed(false)}
          aria-label="Show the music player"
        >
          <Icon name="waves" size={19} />
        </button>
      </div>
    );
  }

  return (
    <div className={`dock${open ? ' is-open' : ''}`} ref={rootRef}>
      {open ? (
        <div className="dock__panel" role="dialog" aria-label="Hymnal">
          <p className="dock__note">
            Played on a synthesised organ, from the tunes themselves — every one
            written before 1900 and long out of copyright.
          </p>

          <ul className="dock__list" ref={listRef}>
            {hymns.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={`dock__track${item.id === hymn.id ? ' is-current' : ''}`}
                  onClick={() => hymnal.select(item.id)}
                >
                  <span className="dock__track-mark" aria-hidden="true">
                    {item.id === hymn.id && playing ? (
                      <span className="dock__bars">
                        <i />
                        <i />
                        <i />
                      </span>
                    ) : (
                      <Icon name="play" size={11} />
                    )}
                  </span>
                  <span className="dock__track-body">
                    <span className="dock__track-title">{item.title}</span>
                    <span className="dock__track-ref">
                      {item.tune} · {item.source}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {mixer ? (
        <div className="dock__mixer" role="dialog" aria-label="Levels">
          <label className="dock__slider">
            <span>Organ</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={music.level}
              onChange={(e) => music.setLevel(Number(e.target.value))}
            />
          </label>
          <button
            type="button"
            className={`dock__bed${music.enabled ? ' is-on' : ''}`}
            onClick={music.toggle}
            aria-pressed={music.enabled}
          >
            <Icon name="waves" size={14} />
            {music.enabled ? 'Ambient bed on' : 'Ambient bed off'}
          </button>
          <p className="dock__hint">
            The ambient bed is the generative chant, separate from the hymns.
          </p>
        </div>
      ) : null}

      <div className="dock__bar">
        <span className={`dock__badge${playing ? ' is-playing' : ''}`} aria-hidden="true">
          <Icon name="waves" size={18} />
        </span>

        <button
          type="button"
          className="dock__meta"
          onClick={() => {
            setOpen((v) => !v);
            setMixer(false);
          }}
          aria-expanded={open}
          aria-label={`${hymn.title} — open the hymnal`}
        >
          <span className="dock__title">{hymn.title}</span>
          <span className="dock__sub">{error ?? hymn.tune}</span>
        </button>

        <button
          type="button"
          className="dock__ctl"
          onClick={hymnal.previous}
          aria-label="Previous hymn"
        >
          <Icon name="skip" size={17} className="dock__back" />
        </button>

        <button
          type="button"
          className="dock__ctl dock__ctl--play"
          onClick={hymnal.toggle}
          disabled={!supported}
          aria-label={playing ? 'Pause the hymn' : 'Play the hymn'}
        >
          <Icon name={playing ? 'pause' : 'play'} size={17} />
        </button>

        <button
          type="button"
          className="dock__ctl"
          onClick={hymnal.next}
          aria-label="Next hymn"
        >
          <Icon name="skip" size={17} />
        </button>

        <button
          type="button"
          className={`dock__ctl${mixer ? ' is-on' : ''}`}
          onClick={() => {
            setMixer((v) => !v);
            setOpen(false);
          }}
          aria-expanded={mixer}
          aria-label="Volume"
        >
          <Icon name={music.enabled ? 'soundOn' : 'soundOff'} size={17} />
        </button>

        <button
          type="button"
          className="dock__ctl"
          onClick={() => {
            hymnal.pause();
            setOpen(false);
            setMixer(false);
            setDismissed(true);
          }}
          aria-label="Close the player"
        >
          <Icon name="close" size={16} />
        </button>

        {/* A hairline of progress along the bottom edge, so the collapsed bar
            still shows how far through the hymn you are. */}
        <span
          className="dock__progress"
          style={{ transform: `scaleX(${Math.max(0, Math.min(1, position))})` }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
